import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Country, I18nModule, WindowRef } from '@spartacus/core';
import { Observable, of, Subscription } from 'rxjs';

@Component({
  selector: 'countries-dropdown',
  standalone: true,
  imports: [CommonModule, I18nModule],
  templateUrl: './countries-dropdown.component.html',
  styleUrls: ['./countries-dropdown.component.scss', './countries-dropdown-countries.component.scss'],
})
export class CountriesDropdownComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('input') input!: ElementRef;
  @ViewChild('container') container!: ElementRef;

  @Input() disabled?: boolean = false;
  @Input() isocode?: string;
  @Input() hideDialingCode?: boolean = false;
  @Input() countries$?: Observable<Country[]> = of([]);
  @Input() form?: UntypedFormGroup;
  @Input() controlName: string = 'country';
  @Input() label: string = 'selectCountries.label';
  @Input() placeholder: string = 'selectCountries.placeholder';
  @Input() displaySelectedContryName: boolean = true;
  @Input() displaySelectedContryDiallingCode: boolean = false;

  country?: Country;
  allCountries: Country[] = [];
  countries: Country[] = [];
  listStatus = false;
  closeListenerActive = false;
  containerIsDisabled = false;
  subscription = new Subscription();

  @Output() _onSelectCountry = new EventEmitter<Country>();

  constructor(private winRef: WindowRef) {}

  ngOnInit(): void {
    this.handleKeyboardEvent();
    this.initCountry();

    this.containerIsDisabled = this.disabled ?? false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isocode'] && changes['isocode'].previousValue !== changes['isocode'].currentValue) {
      const country = this.countries.find(country => country.isocode === this.isocode);

      if (country) {
        this.setCountry(country);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  initCountry() {
    this.subscription.add(
      this.countries$?.subscribe(countries => {
        this.countries = countries;
        this.allCountries = countries;

        const country = countries.find(country => country.isocode === this.isocode);
        country && this.selectCountry(country);
      }),
    );
  }

  open() {
    if (this.containerIsDisabled) {
      return;
    }

    this.containerIsDisabled = true;
    this.listStatus = true;
    const $input = this.input.nativeElement as HTMLInputElement;

    if (this.country) {
      $input.value = this.country.name || '';
    }

    setTimeout(() => {
      $input.select();
      this.closeListenerActive = true;
    }, 50);

    setTimeout(() => {
      this.containerIsDisabled = false;
    }, 500);
  }

  close() {
    this.listStatus = false;
    this.closeListenerActive = false;
  }

  selectCountry(country: Country | undefined) {
    this.close();
    this.setCountry(country);
  }

  setCountry(country: Country | undefined) {
    this.country = country;
    this._onSelectCountry.emit(country);
  }

  filter(query: string) {
    if (query === ``) {
      this.setCountry(undefined);
    }

    this.countries =
      query === ``
        ? this.allCountries
        : this.allCountries.filter(country =>
            (country.name || '').toLocaleLowerCase().includes(query.toLocaleLowerCase()),
          );
  }

  handleKeyboardEvent() {
    const _this = this;

    this.winRef.isBrowser() &&
      this.winRef.document.body.addEventListener('keyup', function (event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          _this.close();
        }
      });
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const clickedElement = event.target as HTMLElement;

    if (!this.closeListenerActive) {
      return;
    }

    if (this.container && !this.container.nativeElement.contains(clickedElement)) {
      this.close();
    }
  }
}
